import { Box, Typography } from '@mui/material';
import { useEffect } from 'react';
import { useBetween } from 'use-between';
import { IFeatureDiameterDto } from '../../product-filters-menu/interfaces/feature-diameter-dto.interface';
import { ProductFiltersUseBetween } from '../../product-filters-menu/use-between/product-filters-use-between';
import { Utils } from '../../../shared/constants/utilities.constants';
import DiameterFilterSlider from '../../product-filters-menu/sliders/diameter-filter-slider';
import DiameterCheckboxDropdown from '../../product-filters-menu/dropdow-list/diameter-checkbox-dropdown';
import { getDiameterList } from '../../product-filters-menu/dropdow-list/diameter-checkbox.service';
import { NpsToDn } from '../../../shared/constants/pipe-diameter-conversion.constants';
import { getRangeFilter } from '../../product-filters-menu/product-filters.service';
import { filterItemSx, filterLabelSx } from './ordering-table-filters.styles';

export interface IOrderingDiameterFilterProps {
    diameterFilter: IFeatureDiameterDto;
    clear?: number;
}

// Reuses DiameterFilter's state and business logic, rendering mm/dn/nps as separate filter slots. Kept as
// its own component so this layout stays independent of ProductsSecondLevel's DiameterFilter.
const OrderingDiameterFilter: React.FC<IOrderingDiameterFilterProps> = ({ diameterFilter, clear }) => {
    const { setSelectedNpsAndDnDiameter } = useBetween(ProductFiltersUseBetween);
    const { setDiameterMin } = useBetween(ProductFiltersUseBetween);
    const { setDiameterMax } = useBetween(ProductFiltersUseBetween);
    const { setFeaturesFilters } = useBetween(ProductFiltersUseBetween);
    const { diameterRangeFromTo, setDiameterRangeFromTo } = useBetween(ProductFiltersUseBetween);
    const { currentFeatureId, setCurrentFeatureId } = useBetween(ProductFiltersUseBetween);
    const { diameterList, setDiameterList } = useBetween(ProductFiltersUseBetween);

    // Scope the standard NPS/DN size table down to the sizes this product's variants actually have;
    // fall back to the full table if the feature has no occurring values to scope by.
    const getScopedDiameterList = () => {
        const fullList = getDiameterList();
        const occurringDnValues = diameterFilter.dn.availableEnumValues ?? [];
        if (occurringDnValues.length === 0) {
            return fullList;
        }
        const scoped = fullList.filter((item) => occurringDnValues.includes(item.config.value.toString()));
        return scoped.length > 0 ? scoped : fullList;
    };

    const init = () => {
        const diameterListCopy = getScopedDiameterList();
        const diameterMinValue = diameterFilter.mm.minValue ?? NpsToDn._1_8.value;
        setCurrentFeatureId(diameterFilter.mm.id);
        setDiameterList(diameterListCopy);
        setSelectedNpsAndDnDiameter(Utils.clone(diameterListCopy));
        setDiameterMin(diameterMinValue);
        setDiameterMax(diameterFilter.mm.maxValue);
        setDiameterRangeFromTo([diameterMinValue, diameterFilter.mm.maxValue]);
    };

    const handleRangeChange = () => {
        setFeaturesFilters((prevFilters) =>
            getRangeFilter(
                currentFeatureId as string,
                diameterRangeFromTo[0].toString(),
                diameterRangeFromTo[1].toString(),
                prevFilters
            )
        );
    };

    useEffect(() => {
        handleRangeChange();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [diameterRangeFromTo, currentFeatureId]);

    useEffect(() => {
        if (clear !== undefined && clear > 0) {
            init();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [clear]);

    useEffect(() => {
        init();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <>
            <Box sx={filterItemSx}>
                <Typography variant="body2" noWrap title={diameterFilter.mm.labelInEnglish} sx={filterLabelSx}>
                    {diameterFilter.mm.labelInEnglish}
                </Typography>
                {/* DiameterFilterSlider hardcodes width: 300 - override it to fit this slot. No overflow:hidden
                    and extra padding, or the value-label bubbles at the 0%/100% thumb positions get clipped. */}
                <Box
                    sx={{
                        border: '1px solid rgba(0, 0, 0, 0.23)',
                        borderRadius: 1,
                        px: 3,
                        '& .MuiBox-root': { width: '100% !important' },
                    }}
                >
                    <DiameterFilterSlider />
                </Box>
            </Box>
            <Box sx={filterItemSx}>
                <Typography variant="body2" noWrap title={diameterFilter.dn.labelInEnglish} sx={filterLabelSx}>
                    {diameterFilter.dn.labelInEnglish}
                </Typography>
                {/* DiameterCheckboxDropdown hardcodes width: 300px inline, so only !important can override it. */}
                <Box sx={{ '& .MuiAutocomplete-root': { width: '100% !important' } }}>
                    <DiameterCheckboxDropdown data={diameterList} isFromDn={true} showSelectAll />
                </Box>
            </Box>
            <Box sx={filterItemSx}>
                <Typography variant="body2" noWrap title={diameterFilter.nps.labelInEnglish} sx={filterLabelSx}>
                    {diameterFilter.nps.labelInEnglish}
                </Typography>
                <Box sx={{ '& .MuiAutocomplete-root': { width: '100% !important' } }}>
                    <DiameterCheckboxDropdown data={diameterList} isFromDn={false} showSelectAll />
                </Box>
            </Box>
        </>
    );
};

export default OrderingDiameterFilter;
